import { Dom, animate } from '@brandocms/jupiter'

export default app => ({
  rootMargin: '0% 0% -2% 0%',
  threshold: 0,
  initialDelay: 100,

  runs: {
    booking: {
      threshold: 0.0,
      repeated: true,
      callback: (element, repeated) => {
        if (repeated) {
          const bookingBar = document.querySelector('.booking-bar')
          if (bookingBar) {
            bookingBar.classList.add('is-transitioning')
            setTimeout(() => {
              bookingBar.classList.remove('is-transitioning')
            }, 300)
          }
          document.body.classList.remove('booking-bar-active')
          return
        }

        animate(element, { opacity: 1 }, { duration: 0.35 })
      },
      onExit: (_element, _repeated, meta) => {
        if (meta.direction === 'top') {
          const bookingBar = document.querySelector('.booking-bar')
          if (bookingBar) {
            bookingBar.classList.add('is-transitioning')
            setTimeout(() => {
              bookingBar.classList.remove('is-transitioning')
            }, 300)
          }
          document.body.classList.add('booking-bar-active')
        }
      },
    },

    bookvisit: {
      threshold: 0.0,
      repeated: true,
      onReady: async element => {
        element.setAttribute('data-ready-time', Date.now())

        // Load BookVisit script if not already loaded
        if (!window.BV) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script')
            script.src = '//reservations.bookvisit.com/widget?type=accommodation'
            script.onload = resolve
            script.onerror = reject
            document.head.appendChild(script)
          })
        }

        const language = app.language === 'no' ? 'nb' : 'en-GB'

        // Get config from data attributes
        const config = {
          channelId: element.dataset.channelId || '004704a9-4399-48b7-991e-994bb578b97e',
          widgetLayout: 'StandardWide',
          WidgetContainerId: 'widgetContainer',
          promoCode: 'false',
          corpCode: 'false',
          agentCode: 'false',
          openOption: 'newwindow',
          loginLink: 'false',
          currency: 'NOK',
          languageCulture: language,
          accommodationTypePicker: 'false',
          type: 'single_property',
        }

        const customStyles = `
          [role="application"],
          .bv-widget-container,
          #root,
          * {
            font-family: "Ytri Flare", sans-serif !important;
          }
            
          #bv-widget-container {
            font-size: 18px !important;
          }

          @media (min-width: 0) and (max-width: 767px) {
            #bv-widget-container [class*="gap-[10px]"] {
              gap: 20px !important;
            }
          }

          .text-bv_smallFontSize {
            font-size: 18px !important;
          }

          * {
            border-color: black;
          }

          svg:not(:root).svg-inline--fa, svg:not(:host).svg-inline--fa {
            overflow: visible;
            box-sizing: content-box;
            color: black;
          }

          .text-white {
            color: black;
            font-weight: 350;
          }

          @media (min-width: 0) and (max-width: 767px) {
            #calendar-dialog {
              --bv-bodyColor: 0, 0, 0 !important;
            }

            [role="application"]:not(#bv-widget-container) {
              --bv-bodyColor: 0, 0, 0 !important;
            }

            h2 {
              color: black !important;
            }

            #calendar-dialog h2 {
              color: black !important;
            }
          }

          @media (min-width: 768px) {
            [class*="bg-black/[0.15]"] {
              background-color: rgba(197, 220, 255, 0.4);
            }

            [class*="bg-black/[0.25]"]:hover {
              background-color: rgba(197, 220, 255, 0.1);
            }
          }

          [data-icon="chevron-down"] {
            height: 0.8em !important;
            width: 0.8em !important;
            min-width: 0.8em !important;
          }

          [data-icon="calendar"] {
            margin-top: 3px;
          }

          [data-icon="user"] {
            margin-top: 3px;
          }

          .bv_inputBorder {
            clip-path: polygon(
              5px 0,
              calc(100% - 5px) 0,
              100% 5px,
              100% calc(100% - 5px),
              calc(100% - 5px) 100%,
              5px 100%,
              0 calc(100% - 5px),
              0 5px
            );
          }
        `

        // Initialize widget
        await window.BV.SPWidget({ ...config })

        // Wait for shadow roots to be created
        await new Promise(resolve => setTimeout(resolve, 1000))

        const container = document.getElementById('widgetContainer')
        const shadowRoot = container?.shadowRoot

        const portalContainer = document.getElementById(
          'bv-widget-portal-container-widgetContainer',
        )
        const portalShadowRoot = portalContainer?.shadowRoot

        // Inject into main shadow root
        if (shadowRoot) {
          const style = document.createElement('style')
          style.textContent = customStyles
          shadowRoot.appendChild(style)

          const rootEl = shadowRoot.getElementById('root')
          if (rootEl) {
            rootEl.style.setProperty('--bv-bodyFontFamily', '"Ytri Flare", sans-serif')
            rootEl.style.setProperty('--bv-bodyColor', '197, 220, 255')
            rootEl.style.setProperty('--bv-inputBorderWidth', '0px')
          }
        }

        // Function to inject styles into portal
        const injectPortalStyles = portalRoot => {
          if (portalRoot.querySelector('style[data-custom-injected]')) {
            return
          }

          const portalStyle = document.createElement('style')
          portalStyle.setAttribute('data-custom-injected', 'true')
          portalStyle.textContent = customStyles
          portalRoot.appendChild(portalStyle)

          const portalRootEl = portalRoot.getElementById('root')
          if (portalRootEl) {
            portalRootEl.style.setProperty('--bv-bodyFontFamily', '"Ytri Flare", sans-serif')
            portalRootEl.style.setProperty('--bv-bodyColor', '197, 220, 255')
            portalRootEl.style.setProperty('--bv-inputBorderWidth', '0px')
            portalRootEl.style.setProperty('--bv-dividerBorderColor', '197, 220, 255')
          }
        }

        // Inject into portal shadow root if it exists
        if (portalShadowRoot) {
          injectPortalStyles(portalShadowRoot)
        }

        // Watch for portal container to be created
        const portalObserver = new MutationObserver(() => {
          const portal = document.getElementById('bv-widget-portal-container-widgetContainer')
          if (portal?.shadowRoot) {
            injectPortalStyles(portal.shadowRoot)
          }
        })

        portalObserver.observe(document.body, {
          childList: true,
          subtree: true,
        })

        // fade in right away, since we want the patterns to be visible
        // Fade in when ready
        animate(element, { opacity: 1 }, { duration: 0.35 })

        Dom.addClass(element, 'booking-ready')
      },
      callback: async (_element, repeated) => {
        if (repeated) {
          const bookingBar = document.querySelector('.booking-bar')
          if (bookingBar) {
            bookingBar.classList.add('is-transitioning')
            setTimeout(() => {
              bookingBar.classList.remove('is-transitioning')
            }, 300)
          }
          document.body.classList.remove('booking-bar-active')
          return
        }
      },
      onExit: (_element, _repeated, meta) => {
        if (meta.direction === 'top') {
          const bookingBar = document.querySelector('.booking-bar')
          if (bookingBar) {
            bookingBar.classList.add('is-transitioning')
            setTimeout(() => {
              bookingBar.classList.remove('is-transitioning')
            }, 300)
          }
          document.body.classList.add('booking-bar-active')
        }
      },
    },

    smartrecruiters: {
      threshold: 0.0,
      callback: async element => {
        // the run observer unobserves after the first intersection, but guard
        // anyway so a re-init (live preview, ajax) can't mount twice
        if (element.hasAttribute('data-widget-initialized')) {
          return
        }
        element.setAttribute('data-widget-initialized', '')

        // [data-moonwalk-run] is opacity 0 until revealed — bring the block
        // itself in now, the table fades in separately once the jobs land
        animate(element, { opacity: 1 }, { duration: 0.35 })

        // smart_widget.js pulls in its own jQuery, so this is deliberately
        // deferred until the block scrolls into view
        if (typeof window.widget !== 'function') {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script')
            script.src =
              'https://static.smartrecruiters.com/job-widget/1.8.6/script/smart_widget.js'
            script.onload = resolve
            script.onerror = reject
            document.head.appendChild(script)
          })
        }

        const config = {
          company_code: 'RelaisChateaux',
          departments_field_id: '60e338f178bf225846bf7467',
          jobAdType: 'INTERNAL_OR_PUBLIC',
          api_url: 'https://www.smartrecruiters.com',
          job_ad_url: 'https://jobs.smartrecruiters.com',
          auto_height: 'auto',
          auto_width: 'auto',
          job_title: 'true',
          location: 'true',
          type_of_employment: 'false',
          department: 'true',
          published_since: 'false',
          remove_headers: 'false',
          add_search: 'false',
          filter_locations: ['Træna'],
          auth: 'a0b129f1916a42f891a5d725c00c4998',
          trid: 'f7c6e435-d19a-43b0-b6f1-a6854150e17e',

          // also sent as `langCode` to the job API, so the ads come back
          // in the site's language too
          language: app.language === 'no' ? 'no' : 'en',

          // `custom_css_url` and the `bg_color_*`/`txt_color_*` keys are
          // deliberately omitted — the widget would inject an unscoped global
          // stylesheet plus a `#jobWidgetContainer`-scoped <style> block that
          // outranks ours. Styled in modules.css under [b-tpl="jobListings"]
          // instead, using the colors from the original snippet.
        }

        // The widget ships each row as `<tr tabindex="0" onclick="window.open(…)">`
        // with no anchor anywhere, so nothing reads as clickable without hover
        // (ie. never on touch) and Enter/Space do nothing when a row is focused.
        // Promote the job title to a real link using the URL from the onclick.
        const linkifyRows = () => {
          const container = document.getElementById('jobWidgetContainer')
          if (!container) {
            return
          }

          container.querySelectorAll('tr[onclick]').forEach(tr => {
            const cell = tr.querySelector('td.srJobListJobTitle')
            if (!cell || cell.querySelector('a')) {
              return
            }

            const url = tr.getAttribute('onclick').match(/window\.open\(["']([^"']+)["']\)/)
            if (!url) {
              return
            }

            const link = document.createElement('a')
            link.href = url[1]
            link.target = '_blank'
            link.rel = 'noopener'
            link.textContent = cell.textContent.trim()

            // the row's own onclick would fire a second window.open on top of
            // the anchor's navigation, so let the anchor own clicks inside it
            link.addEventListener('click', e => e.stopPropagation())

            cell.textContent = ''
            cell.appendChild(link)

            // focusable-but-not-actionable is worse than not focusable at all —
            // the anchor is the focus target now
            tr.removeAttribute('tabindex')
          })
        }

        // second arg mounts into our own container instead of the widget's
        // insert-after-the-script-tag fallback
        window.widget(config, 'jobWidgetContainer', true, () => {
          linkifyRows()
          Dom.addClass(element, 'jobs-ready')
        })
      },
    },

    loop: {
      threshold: 0.0,
      repeated: true,
      callback: (element, repeated) => {
        if (repeated) {
          element.$loop.play()
          return
        }

        element.style.opacity = 1
      },

      onExit: (element, _repeated) => {
        element.$loop.pause()
      },
    },

    mosaicFan: {
      threshold: 0.1,
      repeated: false,
      callback: element => {
        const inner = element.querySelector('.inner')
        if (!inner) {
          element.style.opacity = 1
          return
        }

        const tiles = inner.querySelectorAll('.tile')
        const innerRect = inner.getBoundingClientRect()
        const centerX = innerRect.width / 2
        const centerY = innerRect.height / 2

        // Calculate each tile's offset from center and set initial position
        tiles.forEach(tile => {
          const tileRect = tile.getBoundingClientRect()
          const tileCx = tileRect.left - innerRect.left + tileRect.width / 2
          const tileCy = tileRect.top - innerRect.top + tileRect.height / 2
          const dx = centerX - tileCx
          const dy = centerY - tileCy

          // Start piled at center
          tile.style.transform = `translateX(${dx}px) translateY(${dy}px)`
          tile.style.opacity = '0'
        })

        // Fade in the container and tiles (still piled at center)
        element.style.opacity = 1
        tiles.forEach(tile => {
          tile.style.transition = 'opacity 0.5s ease'
          tile.style.opacity = '1'
        })

        // Wait, then fan out with staggered timing
        setTimeout(() => {
          tiles.forEach((tile, i) => {
            const delay = i * 0.02
            setTimeout(() => {
              tile.style.transition = `transform 2.8s cubic-bezier(0.1, 1, 0.3, 1)`
              tile.style.transform = 'translateX(0) translateY(0)'
            }, delay * 1000)
          })
        }, 800)
      },
    },

    heroLogo: {
      threshold: 0.0,
      repeated: false,
      onReady: async element => {
        const logoContainer = element.querySelector('[data-hero-logo]')
        const headingEl = element.querySelector('[data-hero-heading]')
        const bgImage = element.querySelector('.bg img')

        if (!logoContainer) return

        // Import and initialize Lottie
        const { default: initializeHeroLottie } = await import('../modules/LogoAnimation')

        // Wait for background image to load
        const waitForImage = () => {
          return new Promise(resolve => {
            if (!bgImage) {
              resolve()
              return
            }
            if (bgImage.complete) {
              resolve()
              return
            }
            bgImage.addEventListener('load', resolve, { once: true })
            bgImage.addEventListener('error', resolve, { once: true })
          })
        }

        await waitForImage()

        // Initialize animation with completion callback
        element.$heroAnim = await initializeHeroLottie(logoContainer, () => {
          // Fade out logo, fade in heading
          animate(logoContainer, { opacity: 0 }, { duration: 0.5 }).then(() => {
            logoContainer.style.display = 'none'
          })
          headingEl.style.transform = 'translateY(15px)'
          animate(headingEl, { opacity: 1 }, { duration: 0.5, delay: 0.2, ease: 'linear' })
          animate(headingEl, { y: 0 }, { duration: 0.5, delay: 0.2, ease: 'circOut' })
        })

        console.log('-> fade in.')
        // Fade in the hero element
        animate(element, { opacity: 1 }, { duration: 0.35 })

        // Play the animation after a short delay
        setTimeout(() => {
          element.$heroAnim.play()
        }, 500)
      },
    },
  },

  walks: {
    default: {
      interval: 0.2,
      duration: 0.35,
      transition: null,
    },

    r: {
      interval: 0.1,
      duration: 0.35,
      transition: null,
    },

    l: {
      interval: 0.08,
      duration: 0.35,
      transition: null,
    },

    u: {
      interval: 0.1,
      duration: 0.35,
      transition: null,
    },

    uFast: {
      interval: 0.03,
      duration: 0.35,
      alphaTween: true,
      transition: {
        from: {
          y: 30,
        },

        to: {
          y: 0,
          ease: [0.215, 0.61, 0.355, 1],
        },
      },
    },

    bigUp: {
      interval: 0.25,
      duration: 0.4,
      transition: null,
    },

    slideInLeft: {
      interval: 0.2,
      duration: 1,
      alphaTween: false,
      transition: {
        from: {
          opacity: 1,
          x: '-100%',
        },

        to: {
          opacity: 1,
          x: '0%',
          ease: [0.215, 0.61, 0.355, 1],
        },
      },
    },

    slideInRight: {
      interval: 0.2,
      duration: 1,
      alphaTween: false,
      transition: {
        from: {
          opacity: 1,
          x: '100%',
        },

        to: {
          opacity: 1,
          x: '0%',
          ease: [0.215, 0.61, 0.355, 1],
        },
      },
    },
  },
})
